import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance.js";
import io from "socket.io-client";
import PageWrapper from "../components/PageWrapper.jsx";
import { LanguageContext } from "../contexts/LanguageContext.js";
import { ToastContainer, toast } from "react-toastify";
import BraunClock from "../components/BraunClock";

const SOCKET_SERVER_URL =
  process.env.REACT_APP_SOCKET_URL || "https://adey.lucky.et";

const LS_CURRENT_GAME_ID_KEY = "bingo_current_game_id";
const LS_SELECTED_CARTELLAS_PREFIX = "bingo_selected_cartellas_";
const LS_CONFIRMED_CARTELLAS_PREFIX = "bingo_confirmed_cartellas_";

const getBallColor = (num) => {
  if (num === "F") return "#999999";
  if (num <= 15) return "#FF3333";
  if (num <= 30) return "#3388FF";
  if (num <= 45) return "#AA33FF";
  if (num <= 60) return "#33FF66";
  return "#FFEE33";
};

const reconstructBoardFrom1D = (flatBoard1D) => {
  const tempGridColMajor = Array(5)
    .fill(null)
    .map(() => Array(5).fill(null));
  const letters = ["B", "I", "N", "G", "O"];
  let currentRawIndex = 0;

  for (let colIdx = 0; colIdx < 5; colIdx++) {
    for (let rowIdx = 0; rowIdx < 5; rowIdx++) {
      if (letters[colIdx] === "N" && rowIdx === 2) {
        tempGridColMajor[colIdx][rowIdx] = "F";
      } else {
        tempGridColMajor[colIdx][rowIdx] =
          flatBoard1D[currentRawIndex] !== undefined
            ? flatBoard1D[currentRawIndex]
            : null;
        currentRawIndex++;
      }
    }
  }
  return tempGridColMajor;
};

const BingoGamePage = (props) => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();

  const translations = {
    en: {
      loadingCartellas: "Loading available cartellas...",
      cartellaTaken: "This cartella is already taken by another player.",
      cartellaSelected: "Cartella selected successfully!",
      cartellaDeselected: "Cartella deselected successfully!",
      noCartellas: "No cartellas available",
      gameCompleted: "Game has completed.",
      gameFailed: "Game cancelled due to insufficient players.",
      noWinner: "All numbers called. Game ended without a Bingo!",
      youWon: "Congratulations! You won {amount} Birr!",
      othersWon: "Game ended. Other player(s) won.",
      stakeDisplay: "{amount} Birr Per Card",
      waitingPlayers: "Waiting for more players... ({count}/2)",
      readyToStart: "Game is ready to start with {count} players!",
      goBack: "Go Back to Stakes",
      loadingStake: "Loading Stake...",
      redirecting: "Redirecting...",
      selectCards: "Select Cartellas",
      loading: "Loading...",
      loadingCartella: "Loading cartella details...",
      selectedCards: "Your Selected Cartellas",
      insufficientBalance: "Insufficient balance! Need ETB {requiredAmount}",
      cardNumber: "Card",
      free: "FREE",
      noCartellaData: "No cartella data available",
      gameNotFound: "Game not found",
      depositNow: "Deposit Now",
      processing: "Processing...",
      total: "Total",
    },
    am: {
      loadingCartellas: "የቢንጎ ካርታዎች በመጫን ላይ...",
      cartellaTaken: "ይህ ካርታ በሌላ ተጫዋች ተይዟል።",
      cartellaSelected: "ካርቴላ በተሳካ ሁኔታ ተመርጧል!",
      cartellaDeselected: "ካርቴላ በተሳካ ሁኔታ ተወግዷል!",
      noCartellas: "ምንም ካርታዎች የሉም",
      gameCompleted: "ጨዋታው ተጠናቅቋል።",
      gameFailed: "ጨዋታው ተሰርዟል።",
      noWinner: "ሁሉም ቁጥሮች ተጠርተዋል።",
      youWon: "እንኳን ደስ አለህ! {amount} ብር አሸነፍክ!",
      othersWon: "ሌሎች ተጫዋቾች አሸነፉ።",
      stakeDisplay: "{amount} ብር በካርድ",
      waitingPlayers: "ተጨማሪ ተጫዋቾችን በመጠበቅ ላይ... ({count}/2)",
      readyToStart: "ጨዋታው ከ{count} ተጫዋቾች ጋር ዝግጁ ነው!",
      goBack: "ወደ ውርርድ ይመለሱ",
      loadingStake: "እርምጃ በመጫን ላይ...",
      redirecting: "በመቀየር ላይ...",
      selectCards: "ካርቴላዎችን ይምረጡ",
      loading: "በመጫን ላይ...",
      selectedCards: "የተመረጡ ካርቴላዎች",
      insufficientBalance: "በቂ ሂሳብ የለም! ETB {requiredAmount} ያስፈልጋል",
      cardNumber: "ካርድ",
      free: "ነጻ",
      noCartellaData: "ምንም ውሂብ የለም",
      gameNotFound: "ጨዋታ አልተገኘም",
      depositNow: "ገንዘብ ያስገቡ",
      processing: "በሂደት ላይ...",
      total: "ጠቅላላ",
    },
    or: {
      loadingCartellas: "Kaardota Bingo ni fe'ama...",
      cartellaTaken: "Kaardii kun taphataa biraatiin qabameera.",
      cartellaSelected: "Cartella milkaa'inaan filatame!",
      cartellaDeselected: "Cartella milkaa'inaan irraa deebifame!",
      noCartellas: "Kaardota hin jiru",
      gameCompleted: "Tapha xumureera.",
      gameFailed: "Tapha dhaabbateera.",
      noWinner: "Lakkoofsota hundaa waamame.",
      youWon: "Baga gammaddan! {amount} Birr moo'atte!",
      othersWon: "Taphataa birootiin moo'ate.",
      stakeDisplay: "{amount} Birr Kaardii irratti",
      waitingPlayers: "Taphataa dabalataa eegama... ({count}/2)",
      readyToStart: "Tapha {count} taphataa waliin jalqachuu ni danda'ama!",
      goBack: "Seenaa Filannoo Deebi'i",
      loadingStake: "Seenaa fe'amaa jira...",
      redirecting: "Deebi'uun jira...",
      selectCards: "Kaardota Filadhu",
      loading: "Fe'amaa jira...",
      selectedCards: "Kaardota Filataman",
      insufficientBalance: "Maallaqni hin gahu! ETB {requiredAmount} barbaachisa",
      cardNumber: "Kaardii",
      free: "Bilisa",
      noCartellaData: "Dataan hin jiru",
      gameNotFound: "Tapha hin argamne",
      depositNow: "Maallaqa Saavi",
      processing: "Hojiirraa jira...",
      total: "Iddoo",
    },
    ti: {
      loadingCartellas: "ካርታ ቢንጎ እዚ ዝተሳተፈ እዩ...",
      cartellaTaken: "እዚ ካርታ ብካልእ ተጻወቲ ተወሲዱ ኣሎ።",
      cartellaSelected: "ካርቴላ ብዓወት ተመሪጹ!",
      cartellaDeselected: "ካርቴላ ብዓወት ተወጊዱ!",
      noCartellas: "ካርታ የለን",
      gameCompleted: "ጨዋታ ተዛዚሙ ኣሎ።",
      gameFailed: "ጨዋታ ተቋሪጻ ኣሎ።",
      noWinner: "ኩሎም ቁጽርታት ተውሳኹ።",
      youWon: "እንቋዕ ሓጎስኩም! {amount} ብር ተዓዊትኩም!",
      othersWon: "ካልኦት ተጻወቲ ተዓዊቶም።",
      stakeDisplay: "{amount} ብር ኣብ ካርታ",
      waitingPlayers: "ንዝያዳ ተጻወትቲ እዩ ዝጽበ... ({count}/2)",
      readyToStart: "ጨዋታ ምስ {count} ተጻወቲ ክጀምር ድሉ እዩ!",
      goBack: "ናብ ድሕሪ ተመለሱ",
      loadingStake: "ድሕሪ እዩ ዝጽበ...",
      redirecting: "በመቀየር ላይ...",
      selectCards: "ካርቴላታት ምረጹ",
      loading: "ይጽዓን...",
      selectedCards: "ዝተመርጹ ካርቴላታት",
      insufficientBalance: "ኣብ ባላንስ ገንዘብ የቐንየለይ! ETB {requiredAmount} የድልየካ",
      cardNumber: "ካርድ",
      free: "ናጻ",
      noCartellaData: "ሓበሬታ የለን",
      gameNotFound: "ጸወታ ኣይተረኽበን",
      depositNow: "ገንዘብ ኣእትዉ",
      processing: "ምዝጋር...",
      total: "ድምር",
    },
    wo: {
      loadingCartellas: "Kaardota Bingo ni fe'ama...",
      cartellaTaken: "Kaardii kun taphataa biraatiin qabameera.",
      cartellaSelected: "Cartella milkaa'inaan filatame!",
      cartellaDeselected: "Cartella milkaa'inaan irraa deebifame!",
      noCartellas: "Kaardota hin jiru",
      gameCompleted: "Tapha xumureera.",
      gameFailed: "Tapha dhaabbateera.",
      noWinner: "Lakkoofsota hundaa waamame.",
      youWon: "Baga gammaddan! {amount} Birr moo'atte!",
      othersWon: "Taphataa birootiin moo'ate.",
      stakeDisplay: "{amount} Birr Kaardii irratti",
      waitingPlayers: "Taphataa dabalataa eegama... ({count}/2)",
      readyToStart: "Tapha {count} taphataa waliin jalqachuu ni danda'ama!",
      goBack: "Seenaa Filannoo Deebi'i",
      loadingStake: "Seenaa fe'amaa jira...",
      redirecting: "Deebi'uun jira...",
      selectCards: "Kaardota Filadhu",
      loading: "Fe'amaa jira...",
      selectedCards: "Kaardota Filataman",
      insufficientBalance: "Maallaqni hin gahu! ETB {requiredAmount} barbaachisa",
      cardNumber: "Kaardii",
      free: "Bilisa",
      noCartellaData: "Dataan hin jiru",
      gameNotFound: "Tapha hin argamne",
      depositNow: "Maallaqa Saavi",
      processing: "Hojiirraa jira...",
      total: "Iddoo",
    },
  };

  const t = useCallback(
    (key, variables = {}) => {
      let translation = translations[language]?.[key] || translations.en[key] || key;
      Object.keys(variables).forEach((varName) => {
        translation = translation.replace(`{${varName}}`, variables[varName]);
      });
      return translation;
    },
    [language],
  );

  const getTranslatedGameStatusKey = useCallback((status) => {
    switch (status) {
      case "completed":
        return "gameCompleted";
      case "failed_start":
        return "gameFailed";
      default:
        return status;
    }
  }, []);

  const initialGameIdFromLocation = location.state?.gameId;
  const initialGameIdFromLS = localStorage.getItem(LS_CURRENT_GAME_ID_KEY);

  const [gameId, setGameId] = useState(() => {
    const idToUse = initialGameIdFromLocation || initialGameIdFromLS;
    if (idToUse) {
      return parseInt(idToUse, 10);
    }
    return null;
  });

  const [confirmedCartellaIdsFromBackend, setConfirmedCartellaIdsFromBackend] = useState([]);
  const [currentStakeAmount, setCurrentStakeAmount] = useState(location.state?.stakeAmount || null);
  const [occupiedCartellas, setOccupiedCartellas] = useState(new Set());
  const [allCartellaIds, setAllCartellaIds] = useState([]);
  const [isLoadingGame, setIsLoadingGame] = useState(true);
  const [isUpdatingCartella, setIsUpdatingCartella] = useState(false);
  const dataVersion = useRef(0);
  const socketRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const hasNavigatedAway = useRef(false);

  const [gameStatus, setGameStatus] = useState(null);
  const [gameNextDrawTime, setGameNextDrawTime] = useState(null);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [playersCount, setPlayersCount] = useState(0);
  const [isWaitingForGameStart, setIsWaitingForGameStart] = useState(false);
  const [cartellaBoards, setCartellaBoards] = useState([]);
  const [showFooter, setShowFooter] = useState(true);

  const showToast = useCallback((message, type = "error", forceDisplay = false) => {
    if (process.env.NODE_ENV !== "development" && !forceDisplay) return;
    toast[type](message, {
      position: "top-right",
      autoClose: 5000,
      toastId: message,
    });
  }, []);

  const cleanupAndNavigate = useCallback(
    (targetPath, state, clearGameData = false) => {
      if (hasNavigatedAway.current) return;
      hasNavigatedAway.current = true;
      
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (clearGameData && gameId) {
        localStorage.removeItem(LS_CURRENT_GAME_ID_KEY);
        localStorage.removeItem(`${LS_SELECTED_CARTELLAS_PREFIX}${gameId}`);
        localStorage.removeItem(`${LS_CONFIRMED_CARTELLAS_PREFIX}${gameId}`);
      }
      setIsWaitingForGameStart(false);
      navigate(targetPath, { state: state });
    },
    [navigate, gameId],
  );

  const calculateCountdown = useCallback((drawTime) => {
    if (!drawTime) return null;
    const timeLeftMs = new Date(drawTime).getTime() - Date.now();
    if (timeLeftMs <= 0) return 0;
    return Math.floor(timeLeftMs / 1000);
  }, []);

  const fetchGameState = useCallback(
    async (currentGameId, silent = false) => {
      if (hasNavigatedAway.current) return;
      if (!currentGameId || isNaN(currentGameId)) {
        if (!silent) setIsLoadingGame(false);
        return;
      }
      try {
        const [
          gameStateResponse,
          allIdsResponse,
          occupiedResponse,
          userSelectionsResponse,
        ] = await Promise.all([
          axiosInstance.get(`/game/${currentGameId}/state`),
          axiosInstance.get(`/game/${currentGameId}/all-cartella-ids`),
          axiosInstance.get(`/game/${currentGameId}/occupied-cartellas`),
          axiosInstance.get(`/game/${currentGameId}/my-selected-cartellas`),
        ]);

        const fetchedGame = gameStateResponse.data?.success && gameStateResponse.data.game;
        const fetchedAllIds = allIdsResponse.data?.success && Array.isArray(allIdsResponse.data.allCartellaIds)
          ? allIdsResponse.data.allCartellaIds : [];
        const fetchedOccupied = occupiedResponse.data?.success && Array.isArray(occupiedResponse.data.occupiedCartellas)
          ? new Set(occupiedResponse.data.occupiedCartellas) : new Set();
        const fetchedConfirmedCartellaIds = userSelectionsResponse.data?.success
          ? userSelectionsResponse.data.selectedCartellaIds || [] : [];

        if (fetchedGame && !hasNavigatedAway.current) {
          setGameStatus(fetchedGame.status);
          setGameNextDrawTime(fetchedGame.next_draw_time);
          setCurrentStakeAmount(fetchedGame.stake_amount);
          setAllCartellaIds(fetchedAllIds);
          setOccupiedCartellas(fetchedOccupied);
          setConfirmedCartellaIdsFromBackend(fetchedConfirmedCartellaIds);
          setPlayersCount(fetchedGame.total_players || 0);
          dataVersion.current += 1;

          if (fetchedGame.status === "active" && fetchedConfirmedCartellaIds.length > 0 && !hasNavigatedAway.current) {
            setIsWaitingForGameStart(true);
            cleanupAndNavigate(`/play-cartella`, {
              gameId: currentGameId,
              cartellaIds: fetchedConfirmedCartellaIds,
              stakeAmount: fetchedGame.stake_amount,
            }, false);
            return;
          } else if ((fetchedGame.status === "completed" || fetchedGame.status === "failed_start") && !hasNavigatedAway.current) {
            setIsWaitingForGameStart(true);
            cleanupAndNavigate("/stake-selection", {
              message: t(getTranslatedGameStatusKey(fetchedGame.status)),
            }, true);
            return;
          }
        }
      } catch (err) {
        if (!silent && !hasNavigatedAway.current) {
          if (err.response?.status === 404) {
            setIsWaitingForGameStart(true);
            setTimeout(() => cleanupAndNavigate("/stake-selection", {}, true), 2000);
          } else if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem("token");
            setIsWaitingForGameStart(true);
            cleanupAndNavigate("/login", {}, true);
          }
        }
      } finally {
        if (!silent && !hasNavigatedAway.current) setIsLoadingGame(false);
      }
    },
    [cleanupAndNavigate, t, getTranslatedGameStatusKey],
  );

  const loadInitialGameData = useCallback(async () => {
    setIsLoadingGame(true);
    let currentActiveGameId = gameId;

    if (location.state?.gameId) {
      currentActiveGameId = location.state.gameId;
      setGameId(currentActiveGameId);
      localStorage.setItem(LS_CURRENT_GAME_ID_KEY, currentActiveGameId);
    } else if (initialGameIdFromLS) {
      currentActiveGameId = parseInt(initialGameIdFromLS, 10);
      setGameId(currentActiveGameId);
    } else {
      setTimeout(() => cleanupAndNavigate("/stake-selection"), 2000);
      setIsLoadingGame(false);
      return;
    }

    if (currentActiveGameId) {
      await fetchGameState(currentActiveGameId);
    } else {
      setIsLoadingGame(false);
    }
  }, [gameId, initialGameIdFromLS, location.state, cleanupAndNavigate, fetchGameState]);

  useEffect(() => {
    loadInitialGameData();
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [loadInitialGameData]);

  useEffect(() => {
    if (gameId && confirmedCartellaIdsFromBackend !== null && !hasNavigatedAway.current) {
      localStorage.setItem(`${LS_CONFIRMED_CARTELLAS_PREFIX}${gameId}`, JSON.stringify(confirmedCartellaIdsFromBackend));
    }
  }, [confirmedCartellaIdsFromBackend, gameId]);

  useEffect(() => {
    if (gameId && !hasNavigatedAway.current) localStorage.setItem(LS_CURRENT_GAME_ID_KEY, gameId);
  }, [gameId]);

  useEffect(() => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    if (gameNextDrawTime && gameStatus === "pending" && !hasNavigatedAway.current) {
      let initialCountdownValue = calculateCountdown(gameNextDrawTime);
      setCountdownTimer(initialCountdownValue);
      countdownIntervalRef.current = setInterval(() => {
        if (hasNavigatedAway.current) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          return;
        }
        setCountdownTimer((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
            fetchGameState(gameId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (gameStatus === "active" && confirmedCartellaIdsFromBackend.length > 0 && !hasNavigatedAway.current) {
      setIsWaitingForGameStart(true);
      cleanupAndNavigate(`/play-cartella`, {
        gameId: gameId,
        cartellaIds: confirmedCartellaIdsFromBackend,
        stakeAmount: currentStakeAmount,
      }, false);
    } else {
      setCountdownTimer(null);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [gameStatus, gameNextDrawTime, gameId, confirmedCartellaIdsFromBackend, calculateCountdown, cleanupAndNavigate, fetchGameState, currentStakeAmount]);

  useEffect(() => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (gameId && gameStatus === "pending" && !hasNavigatedAway.current) {
      pollingIntervalRef.current = setInterval(() => fetchGameState(gameId), 3000);
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [gameId, gameStatus, fetchGameState]);

  useEffect(() => {
    if (!gameId || hasNavigatedAway.current) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setTimeout(() => cleanupAndNavigate("/login", {}, true), 1000);
      return;
    }

    if (socketRef.current) socketRef.current.disconnect();

    const newSocket = io(SOCKET_SERVER_URL, {
      auth: { token: token },
      query: { gameId: gameId },
      transports: ["websocket", "polling"],
    });

    newSocket.on("gameUpdate", (data) => {
      if (hasNavigatedAway.current) return;
      if (data.nextDrawTime) {
        const timeRemainingMs = new Date(data.nextDrawTime).getTime() - Date.now();
        setCountdownTimer(Math.max(0, Math.floor(timeRemainingMs / 1000)));
      }
      if (data.gameStatus) setGameStatus(data.gameStatus);
      if (data.playerCount !== undefined) setPlayersCount(data.playerCount);
      if (data.gameStatus === "active" && confirmedCartellaIdsFromBackend.length > 0 && !hasNavigatedAway.current) {
        setIsWaitingForGameStart(true);
        cleanupAndNavigate(`/play-cartella`, {
          gameId: gameId,
          cartellaIds: confirmedCartellaIdsFromBackend,
          stakeAmount: currentStakeAmount,
        }, false);
      }
      fetchGameState(gameId);
    });

    newSocket.on("gameEnd", (data) => {
      if (hasNavigatedAway.current) return;
      let message = data.message || t("gameCompleted");
      if (data.endStatus === "failed_start") {
        message = data.message || t("gameFailed");
      } else if (data.noWinner) {
        message = data.message || t("noWinner");
      } else if (data.winners && data.winners.length > 0) {
        const currentUserId = localStorage.getItem("userId");
        const currentUserWon = data.winners.some((w) => w.userId.toString() === currentUserId);
        if (currentUserWon) {
          const userWinAmount = data.winners.find((w) => w.userId.toString() === currentUserId)?.winAmount;
          message = t("youWon", { amount: parseFloat(userWinAmount).toFixed(2) });
          showToast(message, "success", true);
        } else {
          message = t("othersWon");
          showToast(message, "info", true);
        }
      }
      setIsWaitingForGameStart(true);
      cleanupAndNavigate("/stake-selection", { message: message }, true);
    });

    newSocket.on("error", (message) => {
      if (hasNavigatedAway.current) return;
      if (message.includes("Authentication failed.")) {
        localStorage.removeItem("token");
        setIsWaitingForGameStart(true);
        cleanupAndNavigate("/login", {}, true);
      }
    });

    socketRef.current = newSocket;
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [gameId, confirmedCartellaIdsFromBackend, cleanupAndNavigate, fetchGameState, t, showToast, currentStakeAmount]);

  useEffect(() => {
    const fetchSelectedCartellaBoards = async () => {
      if (confirmedCartellaIdsFromBackend.length === 0 || hasNavigatedAway.current) {
        setCartellaBoards([]);
        return;
      }

      try {
        const fetchedBoards = [];
        for (const cId of confirmedCartellaIdsFromBackend) {
          const boardResponse = await axiosInstance.get(`/game/cartella-board-details/${cId}`);
          if (boardResponse.data.success && boardResponse.data.cartella) {
            fetchedBoards.push({
              id: cId,
              number: cId,
              board: reconstructBoardFrom1D(boardResponse.data.cartella),
            });
          }
        }
        if (!hasNavigatedAway.current) setCartellaBoards(fetchedBoards);
      } catch (err) {
        console.error("Failed to fetch selected cartella boards:", err);
        if (!hasNavigatedAway.current) setCartellaBoards([]);
      }
    };

    fetchSelectedCartellaBoards();
  }, [confirmedCartellaIdsFromBackend]);

  const handleNumberClick = useCallback(
    async (cartellaId) => {
      if (isLoadingGame || isUpdatingCartella || hasNavigatedAway.current) return;
      if (!gameId) return;

      const isOccupiedByOther = occupiedCartellas.has(cartellaId) && !confirmedCartellaIdsFromBackend.includes(cartellaId);
      if (isOccupiedByOther) {
        showToast(t("cartellaTaken"), "info");
        return;
      }

      const isCurrentlySelected = confirmedCartellaIdsFromBackend.includes(cartellaId);
      let newSelection;
      if (isCurrentlySelected) {
        newSelection = confirmedCartellaIdsFromBackend.filter((id) => id !== cartellaId);
      } else {
        newSelection = [...confirmedCartellaIdsFromBackend, cartellaId];
      }

      setIsUpdatingCartella(true);

      try {
        const response = await axiosInstance.post(`/game/${gameId}/confirm-cartella`, {
          cartellaIds: newSelection,
        });

        if (response.data.success && !hasNavigatedAway.current) {
          setConfirmedCartellaIdsFromBackend(newSelection);
          localStorage.setItem(`${LS_CONFIRMED_CARTELLAS_PREFIX}${gameId}`, JSON.stringify(newSelection));

          const newGameStatus = response.data.gameStatus;
          if (newGameStatus === "active" && newSelection.length > 0 && !hasNavigatedAway.current) {
            setIsWaitingForGameStart(true);
            cleanupAndNavigate("/play-cartella", {
              gameId,
              cartellaIds: newSelection,
              stakeAmount: currentStakeAmount,
            }, false);
          } else {
            showToast(isCurrentlySelected ? t("cartellaDeselected") : t("cartellaSelected"), "success", false);
          }
        }
      } catch (err) {
        if (hasNavigatedAway.current) return;
        let backendMessage = err.response?.data?.message || "";
        let statusCode = err.response?.status;

        if (statusCode === 401 || statusCode === 403) {
          localStorage.removeItem("token");
          setIsWaitingForGameStart(true);
          cleanupAndNavigate("/login", {}, true);
        } else if (statusCode === 400 && (backendMessage.includes("በቂ ሂሳብ የለም") || backendMessage.toLowerCase().includes("insufficient"))) {
          let requiredAmount = (currentStakeAmount * newSelection.length).toFixed(2);
          toast.error(
            <div>
              <p className="mb-3 font-semibold">{t("insufficientBalance", { requiredAmount })}</p>
              <button onClick={() => { toast.dismiss(); navigate("/wallet"); }} className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-600 w-full">
                💰 {t("depositNow")}
              </button>
            </div>,
            { position: "top-center", autoClose: false }
          );
          await fetchGameState(gameId, true);
        } else if (statusCode === 409) {
          showToast(backendMessage || t("cartellaTaken"), "warning");
          await fetchGameState(gameId, true);
        } else {
          showToast(backendMessage || "Network error", "error");
        }
      } finally {
        if (!hasNavigatedAway.current) setIsUpdatingCartella(false);
      }
    },
    [isLoadingGame, isUpdatingCartella, gameId, occupiedCartellas, confirmedCartellaIdsFromBackend, currentStakeAmount, cleanupAndNavigate, showToast, t, navigate, fetchGameState],
  );

  const gridColumns = 8;
  const gridItems = useMemo(() => {
    const sortedIds = [...allCartellaIds].sort((a, b) => a - b);
    const items = [];
    for (let i = 0; i < sortedIds.length; i += gridColumns) {
      items.push(sortedIds.slice(i, i + gridColumns));
    }
    return items;
  }, [allCartellaIds, gridColumns]);

  const totalStake = (currentStakeAmount * confirmedCartellaIdsFromBackend.length || 0).toFixed(2);

  if (isLoadingGame || isWaitingForGameStart) {
    return (
      <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">{isWaitingForGameStart ? t("redirecting") : t("loadingCartellas")}</p>
        </div>
        <ToastContainer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 min-h-screen">
      <ToastContainer />
      
      <div className="flex flex-col h-screen">
        <div className="flex-shrink-0 pt-3 px-3">
          <div className="flex justify-center mb-3">
            <BraunClock countdown={countdownTimer} gameStatus={gameStatus} />
          </div>

          <div className="w-full max-w-md mx-auto bg-purple-800/50 backdrop-blur-sm py-2 px-4 rounded-xl flex items-center justify-between shadow-lg border border-purple-500/30">
            <span className="text-white font-bold text-sm">
              {currentStakeAmount ? t("stakeDisplay", { amount: Number(currentStakeAmount).toFixed(2) }) : t("loadingStake")}
            </span>
            <span className="text-yellow-300 font-bold text-sm">
              {t("selectedCards")}: {confirmedCartellaIdsFromBackend.length}
            </span>
          </div>
        </div>

        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-3 py-3"
          style={{ maxHeight: "calc(100vh - 300px)" }}
        >
          <div className="w-full max-w-md mx-auto bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
            <h3 className="text-white text-center text-md font-bold mb-2">{t("selectCards")}</h3>
            
            {allCartellaIds.length === 0 && !isLoadingGame ? (
              <p className="text-white text-center text-sm">{t("noCartellas")}</p>
            ) : (
              <div className="flex flex-col gap-1">
                {gridItems.map((row, rowIndex) => (
                  <div key={`row-${rowIndex}`} className="flex gap-1 justify-center flex-wrap">
                    {row.map((cartellaId) => {
                      const isSelected = confirmedCartellaIdsFromBackend.includes(cartellaId);
                      const isOccupied = occupiedCartellas.has(cartellaId) && !isSelected;
                      const isDisabled = isLoadingGame || isUpdatingCartella || isOccupied;

                      let bgClass = "bg-white/20 hover:bg-white/30";
                      if (isSelected) bgClass = "bg-green-500 text-white ring-2 ring-green-300";
                      if (isOccupied) bgClass = "bg-red-500/50 cursor-not-allowed opacity-50";

                      return (
                        <button
                          key={cartellaId}
                          onClick={() => !isDisabled && handleNumberClick(cartellaId)}
                          disabled={isDisabled}
                          className={`w-8 h-8 rounded-lg font-bold text-xs transition-all duration-200 ${bgClass} text-white shadow-md hover:shadow-lg ${!isDisabled && !isSelected ? "hover:scale-105" : ""}`}
                        >
                          {cartellaId}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {confirmedCartellaIdsFromBackend.length > 0 && (
          <div className="flex-shrink-0 bg-gradient-to-t from-purple-900/95 to-purple-800/95 backdrop-blur-md border-t border-purple-500/30 shadow-2xl">
            <div className="max-w-md mx-auto px-3 py-2">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-yellow-300 font-bold text-sm">{t("selectedCards")}</h3>
                <div className="flex items-center gap-3">
                  <span className="text-white text-xs">
                    {t("total")}: <span className="text-yellow-300 font-bold">{totalStake} ETB</span>
                  </span>
                  <button
                    onClick={() => setShowFooter(!showFooter)}
                    className="text-gray-400 hover:text-white text-xs"
                  >
                    {showFooter ? "▼" : "▲"}
                  </button>
                </div>
              </div>

              {showFooter && (
                <div className="overflow-x-auto pb-2 max-h-[280px]">
                  <div className="flex gap-3 min-w-max">
                    {cartellaBoards.length === 0 && isUpdatingCartella ? (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{t("processing")}</span>
                      </div>
                    ) : cartellaBoards.length === 0 ? (
                      <p className="text-gray-400 text-sm">{t("noCartellaData")}</p>
                    ) : (
                      cartellaBoards.map((cartellaData) => (
                        <div key={cartellaData.id} className="flex flex-col items-center flex-shrink-0">
                          <h3 className="text-xs font-bold mb-1 text-yellow-200">
                            {t("cardNumber")} {cartellaData.number}
                          </h3>
                          <div 
                            className="grid grid-cols-5 gap-0.5 bg-gray-200 border border-gray-400 p-0.5 rounded-lg mx-auto"
                            style={{ maxWidth: "140px", width: "100%" }}
                          >
                            {["B", "I", "N", "G", "O"].map((letter) => (
                              <div 
                                key={letter} 
                                className="w-full h-auto aspect-square flex items-center justify-center text-[10px] font-extrabold text-white bg-red-700 shadow-md uppercase"
                                style={{ border: "1px solid #a00" }}
                              >
                                {letter}
                              </div>
                            ))}
                            {cartellaData.board.flat().map((cell, idx) => (
                              <div
                                key={`${cartellaData.id}-${idx}`}
                                className={`w-full h-auto aspect-square flex items-center justify-center rounded-sm font-bold text-[10px] border border-gray-400 shadow-inner ${
                                  cell === "F" ? "bg-gray-500 text-white" : "bg-white text-gray-800"
                                }`}
                              >
                                {cell === "F" ? t("free") : cell}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-shrink-0 text-center py-2">
          <button
            onClick={() => cleanupAndNavigate("/stake-selection", {}, true)}
            className="bg-purple-700 hover:bg-purple-600 text-white font-bold py-1.5 px-5 rounded-full transition-all duration-200 shadow-lg text-sm"
          >
            ← {t("goBack")}
          </button>
        </div>
      </div>
    </PageWrapper>
  );
};

export default BingoGamePage;